import { post } from '../services/back.service';

/**
 * Sube un archivo directamente a S3 usando una URL firmada PUT.
 *
 * @param {File} file - Archivo a subir
 * @param {string} inspectionId - ID de la inspección
 * @param {string} type - Tipo de imagen (AI, PC, CI, etc.)
 * @param {string} folder - Carpeta destino ('images' o 'attach')
 * @param {string} name - Identificador lógico del campo de carga
 * @returns {Promise<{key: string, original_name: string, stored_name: string, extension: string, format: string, type: string}|null>}
 */
export const uploadWithSignedUrl = async (file, inspectionId, type, folder = 'images', name = file.name) => {
  const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  const defaultContentTypes = {
    bmp: 'image/bmp',
    gif: 'image/gif',
    heic: 'image/heic',
    heif: 'image/heif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    pdf: 'application/pdf',
    png: 'image/png',
    webp: 'image/webp',
  };
  const contentType = file.type || defaultContentTypes[extension] || 'application/octet-stream';
  const payload = {
    filename: file.name,
    content_type: contentType,
    file_size: file.size,
    folder,
    inspection_id: inspectionId,
    name,
    type,
  };

  const signed = await post('/peritaje/signed-upload-url/', payload);
  if (!signed.success) {
    throw new Error(signed.message || 'No se pudo obtener la URL firmada');
  }

  const { url, key, headers } = signed.data;

  const ext = extension.slice(0, 4) || 'bin';
  // Referencia para el FileField del backend: SIN el prefijo `media/`
  // (el storage lo agrega solo al resolver S3/local). Si el backend envía
  // file_field_name se usa tal cual; si no, se normaliza el key.
  const fileRef = signed.data.file_field_name || key.replace(/^media\//, '');
  const image = {
    key: fileRef,
    original_name: file.name,
    stored_name: `${type}-id-${inspectionId}.${ext}`,
    extension: ext,
    format: `data:${contentType}`,
    type,
    name,
  };

  if (signed.data.already_uploaded) {
    return { ...image, already_uploaded: true };
  }

  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: headers || { 'Content-Type': file.type },
  });

  if (!response.ok) {
    throw new Error(`Error al subir archivo a S3: ${response.statusText}`);
  }

  const saved = await post(`/peritaje/modifyinspection/${folder === 'attach' ? 'attach' : 'pictures'}/${inspectionId}`, {
    type,
    images: [image],
  });
  if (!saved.success) {
    throw new Error(saved.message || 'No se pudo guardar la referencia del archivo');
  }

  return image;
};
